import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Bandage, BedDouble, Ribbon, HeartPulse, Activity, ShieldCheck, Users, Stethoscope } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { usePageMeta } from "../lib/api";

const SUPPLEMENTAL = [
  { icon: Bandage, title: "Accident", text: "Unexpected accidents can create unexpected expenses. Accident coverage is designed to help with the costs that follow a covered injury." },
  { icon: Activity, title: "Critical Illness", text: "Explore protection designed around certain covered serious illnesses — built to help while you focus on recovery." },
  { icon: Ribbon, title: "Cancer", text: "Learn how certain supplemental policies can provide benefits after a covered diagnosis, when families need breathing room most." },
  { icon: HeartPulse, title: "Heart / Stroke", text: "Explore available protection designed around qualifying covered heart and stroke events." },
  { icon: BedDouble, title: "Hospital Indemnity", text: "Coverage that may provide defined benefits related to covered hospital events and stays." },
];

const LIFE = [
  { title: "Term Life", text: "Straightforward protection for a defined period — often the most affordable way to protect the years that matter most." },
  { title: "Permanent Life", text: "Where offered, lifelong coverage options that can build value over time. We'll explain the tradeoffs in plain language." },
  { title: "Final Expense", text: "Where appropriate, smaller policies designed to keep final costs from becoming a family's burden." },
  { title: "Family Protection", text: "Coverage shaped around who depends on you — income, mortgage, childcare, and the life you'd want for them." },
];

const Group = ({ id, eyebrow, title, lead, children, testId }) => (
  <section id={id} className="scroll-mt-24 border-t border-ink/10 py-16 sm:py-20 first:border-t-0">
    <Reveal className="max-w-3xl">
      <p className="eyebrow-dark mb-3">{eyebrow}</p>
      <h2 className="h-display text-3xl text-ink sm:text-4xl">{title}</h2>
      <p className="mt-4 text-base leading-relaxed text-primer sm:text-lg">{lead}</p>
    </Reveal>
    <div data-testid={testId} className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
      {children}
    </div>
    <Reveal className="mt-10">
      <Link to="/contact" data-testid={`${id}-cta`} className="btn-lime">
        Talk Through My Options <ArrowRight className="h-4 w-4" />
      </Link>
    </Reveal>
  </section>
);

const Services = () => {
  const { hash } = useLocation();
  usePageMeta(
    "Insurance Protection Options | Show Me Shield Missouri",
    "Supplemental, accident, critical illness, cancer, heart/stroke, hospital indemnity, life and health insurance options for Missouri individuals and families — explained in plain language.",
    { path: "/services" }
  );

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100);
    }
  }, [hash]);

  return (
    <main className="bg-paper">
      <div className="border-b border-ink/10 bg-ink grid-lines">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20">
          <Reveal>
            <p className="eyebrow mb-4">Protection</p>
            <h1 className="h-display text-4xl text-white sm:text-5xl">Your insurance should make sense.</h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-zinc-400 sm:text-lg">
              Here&apos;s what we help Missouri individuals and families explore — explained the way
              we&apos;d explain it across a kitchen table. No single product is right for everyone,
              and we&apos;ll never pretend otherwise.
            </p>
          </Reveal>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <Group
          id="supplemental"
          eyebrow="Supplemental Insurance"
          title="Protection beyond major medical insurance."
          lead="Health insurance helps with healthcare. Life still has bills. Supplemental coverage is designed around that gap — depending on the policy and covered event, benefits may provide cash you can use where you need it most."
          testId="services-grid-container"
        >
          {SUPPLEMENTAL.map((s, i) => (
            <Reveal key={s.title} delay={i * 60}>
              <div className="card-light h-full p-6">
                <s.icon className="h-7 w-7 text-lime-dim" strokeWidth={1.75} />
                <h3 className="mt-4 font-display text-lg font-bold uppercase tracking-wide text-ink">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-primer">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </Group>

        <Group
          id="life"
          eyebrow="Life Insurance"
          title="Protect the people who depend on you."
          lead="Life insurance isn't about you — it's about who you'd leave behind. We'll help you understand the main types and what each is actually for."
          testId="life-grid"
        >
          {LIFE.map((s, i) => (
            <Reveal key={s.title} delay={i * 60}>
              <div className="card-light h-full p-6">
                <ShieldCheck className="h-7 w-7 text-lime-dim" strokeWidth={1.75} />
                <h3 className="mt-4 font-display text-lg font-bold uppercase tracking-wide text-ink">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-primer">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </Group>

        <Group
          id="health"
          eyebrow="Health Insurance"
          title="Health coverage should make sense before you need it."
          lead="Individual and family health coverage comes with real tradeoffs — premiums, deductibles, networks. We walk through them with you, so there are no surprises later."
          testId="health-grid"
        >
          {[
            { icon: Stethoscope, title: "Individual Coverage", text: "Options for people buying their own health insurance — explained around how you actually use care." },
            { icon: Users, title: "Family Coverage", text: "Coverage shaped around the whole household — kids, parents, and everything in between." },
            { icon: ShieldCheck, title: "Coverage Reviews", text: "Already covered? Let's look at what your current plan actually does — and what it leaves to you." },
          ].map((s, i) => (
            <Reveal key={s.title} delay={i * 60}>
              <div className="card-light h-full p-6">
                <s.icon className="h-7 w-7 text-lime-dim" strokeWidth={1.75} />
                <h3 className="mt-4 font-display text-lg font-bold uppercase tracking-wide text-ink">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-primer">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </Group>
      </div>
      <div className="pb-8" />
    </main>
  );
};

export default Services;
